import React, { forwardRef, useState } from "react";
import { IUserSession } from "types/user.interface";
import Logo from "components/Elements/Logos/Logo";
import { NavLink, useNavigate } from "react-router-dom";
import { getFeedPage } from "util/navigation.util";
import useIsMobile from "hooks/useIsMobile";
import styled from "styled-components";
import CreateProjectButton from "features/projects/components/CreatePostButton/CreatePostButton";
import NotificationHubTrigger from "features/notifications/components/NotificationHubTrigger";
import ExploreIcon from "components/Elements/Icons/ExploreIcon";
import NavBarProfileDropdown from "../NavBarProfileDropdown/NavBarProfileDropdown";
import NavbarSearch from "../NavBarSearch/NavBarSearch";
import MobileNavMenu from "../MobileNavMenu/MobileNavMenu";
import MobileMenuTrigger from "../MobileMenuTrigger";

const NavBarContainer = styled.div`
  display: flex;
  position: fixed !important;
  width: 100vw;
  left: 0;
  top: 0;
  z-index: 99;
  height: 65px;
  padding-left: 20px;
  padding-right: 20px;
  align-items: center;
  display: flex;
  padding-bottom: 0.5rem;
  padding-top: 0.5rem;
  margin: 0 auto;
  z-index: 999;
  justify-content: space-between;
  border-style: solid;
  border-width: 0px 0px 1px 0px;
  border-color: #d9d9d9;
  background-color: #ffffff;

  @media (min-width: 768px) {
    padding-left: 35px;
    padding-right: 35px;
  }

  @media (min-width: 992px) {
    padding-left: 40px;
    padding-right: 40px;
  }
`;

const NavBarCenter = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
`;

const NavBarBrand = styled.div`
  height: 41px;
  display: flex;
  overflow: hidden;
  &:hover {
    cursor: pointer;
  }

  img {
    width: 100%;
  }
`;

const NavBarRight = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 18px;
`;

const NavBarLeft = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`;

const ExploreLink = styled(NavLink)`
  display: flex;
  color: var(--color-black);
  align-items: center;
  gap: 12px;
  font-weight: 400;
`;

const IconContainer = styled.div`
  display: flex;
  gap: 8px;
  position: relative;
`;

type NavBarProps = {
  user: IUserSession;
};

/**
 * Renders Navigation bar
 * - user profile is passed through props
 * - ref for search bar is also passed
 */
const NavBar = forwardRef((props: NavBarProps, ref: any) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  const navigate = useNavigate();
  const feedPage = getFeedPage("user");

  const handleLogo = () => {
    navigate(feedPage);
  };

  const { user } = props;

  const { isMobile } = useIsMobile();

  // render mobile navbar
  if (isMobile) {
    return (
      <>
        <NavBarContainer>
          <NavBarLeft>
            <ExploreIcon size={22} />
          </NavBarLeft>
          <NavBarBrand onClick={handleLogo}>
            <Logo />
          </NavBarBrand>

          <NavBarRight>
            <MobileMenuTrigger
              color="#000"
              isActive={isActive}
              setIsActive={setIsActive}
            />
          </NavBarRight>
        </NavBarContainer>

        {isActive && <MobileNavMenu hide={() => setIsActive(false)} />}
      </>
    );
  }

  // render default navbar
  return (
    <NavBarContainer>
      <NavBarLeft>
        <ExploreLink to="/">
          <ExploreIcon size={32} />
          <p>Explore</p>
        </ExploreLink>
      </NavBarLeft>

      <NavBarCenter>
        <NavBarBrand onClick={handleLogo}>
          <Logo />
        </NavBarBrand>
      </NavBarCenter>

      <NavBarRight>
        <IconContainer>
          <NavbarSearch user={user} ref={ref} />
          <NotificationHubTrigger />
        </IconContainer>
        <CreateProjectButton />
        <NavBarProfileDropdown user={user} />
      </NavBarRight>
    </NavBarContainer>
  );
});

export default NavBar;
